import PropTypes from 'prop-types';
import React, { useLayoutEffect, useRef } from 'react';

import { crashReporterInterface, getLogger } from 'app-components/utils/core';

import TextField from '@mui/material/TextField';

const log = getLogger('SSNField');

export default function SSNField(props) {
  const { value, onChange, format, ...other } = props;

  const ssnRef = useRef(null);
  const ssnCursorRef = useRef(null);

  const handleChange = (e) => {
    try {
      // wrap in a try/catch just to be safe
      let newSSN = e.target.value.replaceAll?.(/[^\d]/g, '') || '';
      const prevCleanedSSN = value?.replaceAll?.(/[^\d]/g, '') || '';
      const cleanedSSN = e.target.value.replaceAll?.(/[^\d]/g, '');
      let newCursorPosition = ssnRef?.current?.selectionStart || 0;

      if (format === 'full') {
        const strippedSSN = e.target.value.replaceAll?.(/[_]+/g, '').replaceAll?.(/-$/g, '') || e.target.value;

        if (newSSN.length > 9) return;

        if (cleanedSSN.length > prevCleanedSSN.length) {
          // added a character
          newCursorPosition = newCursorPosition > strippedSSN.length ? strippedSSN.length : newCursorPosition;
          newCursorPosition =
            newCursorPosition === 7 || newCursorPosition === 4 ? newCursorPosition + 1 : newCursorPosition; // adjust for "-"
        } else {
          // deleted characters
          if (cleanedSSN.length < prevCleanedSSN.length) {
            // the user deleted a digit
            newSSN = cleanedSSN;
          } else if (newCursorPosition < strippedSSN.length) {
            // the user hit delete but it didn't actually delete the character
            // deleted a "-" inside the ssn
            newSSN = strippedSSN.slice(0, newCursorPosition - 1) + strippedSSN.slice(newCursorPosition);
            newSSN = newSSN.replace(/[^\d]/g, '');
            newCursorPosition -= 1;
          } else {
            // the user hit delete but it didn't actually delete the character
            // deleted at or past the end of the ssn
            newSSN = cleanedSSN.slice(0, cleanedSSN.length - 1);
            newCursorPosition = strippedSSN.length - 1;
          }
          newCursorPosition =
            newCursorPosition === 7 || newCursorPosition === 4 ? newCursorPosition - 1 : newCursorPosition; // adjust for "-"
        }

        const first3 = newSSN.length >= 0 ? newSSN.substring(0, Math.min(3, newSSN.length)) : '';
        const second2 = newSSN.length >= 3 ? newSSN.substring(3, Math.min(5, newSSN.length)) : '';
        const third4 = newSSN.length >= 5 ? newSSN.substring(5, cleanedSSN.length) : '';

        newSSN = `${first3 + '_'.repeat(3 - (first3?.length || 0))}-${
          second2 + '_'.repeat(2 - (second2?.length || 0))
        }-${third4 + '_'.repeat(4 - (third4?.length || 0))}`;
      } else {
        // format is 4 digits
        if (newSSN.length > 4) return;
        if (cleanedSSN.length > prevCleanedSSN.length) {
          // user inputted digits
          newSSN = cleanedSSN;
          newCursorPosition = Math.min(newCursorPosition, newSSN.length);
        } else if (cleanedSSN.length < prevCleanedSSN.length) {
          // user actually deleted a digit
          newSSN = cleanedSSN;
          newCursorPosition = Math.min(newCursorPosition, newSSN.length);
        } else {
          // user deleted at end of field
          newSSN = cleanedSSN.slice(0, cleanedSSN.length - 1);
          newCursorPosition = newSSN.length;
        }
        newSSN += '_'.repeat(4 - (newSSN.length || 0));
      }

      ssnCursorRef.current = newCursorPosition;
      onChange(newSSN);
    } catch (error) {
      // just in case something fails we'll reset the field and report the error
      log.log('Error in SSNField: ', error);
      onChange('');
      ssnCursorRef.current = 0;
      crashReporterInterface.reportError(error);
    }
  };

  useLayoutEffect(() => {
    // layout effect so it happens before render
    if (ssnCursorRef.current !== null && ssnRef.current !== null) {
      // 0 is acceptable value
      ssnRef.current.selectionStart = ssnCursorRef.current || 0;
      ssnRef.current.selectionEnd = ssnCursorRef.current || 0;
    }
  }, [value]);

  return (
    <TextField
      value={value}
      onChange={handleChange}
      inputRef={ssnRef}
      label={format === 'full' ? 'Social Security Number' : 'Last 4 of SSN'}
      {...other}
    />
  );
}
SSNField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  format: PropTypes.string,
};
